import { RawDraftContentState } from 'draft-js'
import { IArcEventFilters } from '../constants/arcEvents'
import { enumNarrow } from '../helpers/enumGuard'
import { ClientInfoStatus } from './client'
import { BroadcastMentionType, MentionType } from './mentions'
import { Feedback } from './prospects'

export const ATLAS_AI_SESSION_ID_QUERY_PARAM = 'atlasAiSessionId'
/** Opens the automations page straight onto one automation — how a task's source
 *  indicator links back to the automation that created it. */
export const ARC_AUTOMATION_ID_QUERY_PARAM = 'automationId'
export const AI_BRAND_NAME = 'Arc'
/**
 * @deprecated The multi-bot chatType architecture is retired — the unified
 * agent handles all domains. This string is only written to
 * atlas_chat_history.chat_type for historical back-compat.
 */
export const DEFAULT_CHAT_TYPE = 'financialAnalyst'

export enum AtlasAIMessageStatus {
  QUEUED = 'queued',
  RUNNING = 'running',
  COMPLETED = 'completed',
  /**
   * Soft-success: the agent delivered a usable response and all tools that
   * ran completed their side effects, but the stream errored at the tail
   * (e.g., post-tool turn timed out, context_window_exceeded mid-final-turn).
   * UI renders the response normally with a subtle warning indicator and
   * exposes `errorDetails` on hover/click.
   */
  COMPLETED_WITH_ISSUES = 'completed_with_issues',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/**
 * A turn that has not finished: it is queued behind the worker or running in it.
 *
 * The one definition of "Arc is working on this", which every surface that draws
 * a spinner, stops a run, or sweeps a stuck one has to agree on. Mirrors the
 * status predicates below for subagents and plans.
 */
export const LIVE_MESSAGE_STATUSES = [
  AtlasAIMessageStatus.QUEUED,
  AtlasAIMessageStatus.RUNNING,
] as const

export const isLiveMessageStatus = (status: AtlasAIMessageStatus): boolean =>
  (LIVE_MESSAGE_STATUSES as readonly AtlasAIMessageStatus[]).includes(status)

/**
 * How long a dispatched run is still believed to be starting before it has
 * written a turn row.
 *
 * `maybeDispatchArc` is fire-and-forget and prepares attachments before
 * `resumeArcSession` writes the QUEUED row, so for a moment a run is genuinely in
 * flight with nothing in `atlas_chat_history` to prove it. Generous because a
 * large upload can hold that window for tens of seconds; bounded because a
 * dispatch that died must not leave a spinner turning forever. Shared so the
 * server's "still starting" answer and the client's optimistic seed expire
 * together rather than one outliving the other.
 */
export const ARC_DISPATCH_GRACE_MS = 2 * 60 * 1000

/**
 * Conversational role for AtlasAI chat messages. Values are deliberately the
 * literals expected by the Anthropic / OpenAI SDKs ('user' / 'assistant') so
 * the enum can be passed directly to SDK message builders without conversion.
 */
export enum ChatRole {
  USER = 'user',
  ASSISTANT = 'assistant',
}

export const STALE_RUNNING_THRESHOLD_MS = 15 * 60 * 1000 // 15 minutes

export enum AgentRuntime {
  COMPLETE = 'COMPLETE',
  TOOL_AGENT = 'TOOL_AGENT',
  AUTONOMOUS = 'AUTONOMOUS',
}

/**
 * Discriminator for `agent_runs.context_id`. `context_id` is a free-form,
 * non-FK provenance pointer whose referent table differs by surface (a chat
 * session, a Fireflies transcript, an email, ...). This column names that
 * referent so the polymorphic pointer is actually queryable
 * (`WHERE context_type = 'transcript'`). Stored as text (mirrors AgentName),
 * so adding a new surface needs no migration.
 */
export enum AgentRunContextType {
  /** A chat session id — chat runs and workflow runs invoked from chat. */
  ChatSession = 'chat_session',
  /** A Fireflies transcript id (fireflies_transcripts). */
  Transcript = 'transcript',
  /** A persisted CRM email row id (emails). */
  Email = 'email',
  /** A raw Gmail RFC822 Message-ID from triage (not necessarily persisted). */
  GmailMessage = 'gmail_message',
  /** A borrowing base id (borrowing_bases) — file preprocessing runs. */
  BorrowingBase = 'borrowing_base',
  /** A Salesforce Chatter FeedItem id — the Portfolio News import. */
  ChatterPost = 'chatter_post',
  /** A users id — per-user background runs such as the nightly learned profile. */
  User = 'user',
  /** An error_triage row id — a code-agent investigation started from the Error Center. */
  ErrorTriage = 'error_triage',
  /** A tasks id — the code-agent and synthesis runs of a board triage. */
  Task = 'task',
  /** A source_extraction row id — the one interpretation of an email, meeting or note. */
  SourceExtraction = 'source_extraction',
  /** An event id (one row per real meeting). */
  Meeting = 'meeting',
}

export enum AgentStatus {
  SUCCESS = 'SUCCESS',
  /**
   * Tools committed real side effects AND the model emitted a final answer,
   * but the run terminated abnormally (stream timeout, abort, post-tool turn
   * error). Distinct from FAILED so monitoring doesn't conflate "the user got
   * their result" with "nothing happened."
   */
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  FAILED = 'FAILED',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

/** One model step of a run: where its seconds went. */
export interface IAgentStepTiming {
  /** Local message rewrites before the model call. */
  preStreamMs: number
  /** The model call, from request to its last token. */
  modelMs: number
  /** Running the tools it asked for, before the next step could start. */
  toolMs: number
  tools: string[]
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
}

/** A run's timeline, kept on `agent_runs.timings`. */
export interface IAgentRunTimings {
  /** The caller's work before the run started (loading the session, building prompts). */
  setupMs: number | null
  steps: IAgentStepTiming[]
}

// Canonical identifiers for each agent that logs to agent_runs. Add a new
// variant here whenever a new agent surface is introduced
export enum AgentName {
  ATLAS_AI = 'AtlasAI',
  SOURCE_EXTRACTION = 'SourceExtraction',
  SALESFORCE_FROM_EMAIL = 'SalesforceFromEmail',
  OPS_WRITEUP_WORKFLOW = 'OpsWriteupWorkflow',
  SALESFORCE_MATCHER_WORKFLOW = 'SalesforceMatcherWorkflow',
  AUTOMATED_DEAL_CLEANUP_WORKFLOW = 'AutomatedDealCleanupWorkflow',
  MARKET_INTEL_CANDIDATES_WORKFLOW = 'MarketIntelCandidatesWorkflow',
  MARKET_INTEL_CHATTER_IMPORT = 'MarketIntelChatterImport',
  OPS_PROCESSING = 'OpsProcessing',
  ENTITY_ENRICHMENT = 'EntityEnrichment',
  FILE_PREPROCESSING = 'FilePreprocessing',
  BBC_FILE_CLASSIFY = 'BbcFileClassify',
  BBC_HEADER_MAP = 'BbcHeaderMap',
  LEAD_ENRICHMENT = 'LeadEnrichment',
  DEBTOR_HQ_LOOKUP = 'DebtorHqLookup',
  SAVE_PROSPECT_FILES_FROM_EMAIL = 'SaveProspectFilesFromEmail',
  OPS_CALL_PREP_WORKFLOW = 'OpsCallPrepWorkflow',
  RM_CALL_PREP_WORKFLOW = 'RmCallPrepWorkflow',
  FEEDBACK_FOLLOWUPS = 'FeedbackFollowUps',
  FEEDBACK_CLASSIFY = 'FeedbackClassify',
  DREAM_CYCLE = 'DreamCycle',
  LEARNED_PROFILE = 'LearnedProfile',
  MARKET_NEWS_BRAND_MATCHER = 'MarketNewsBrandMatcher',
  CALL_TYPE_CLASSIFIER = 'CallTypeClassifier',
  AIM_ACTED_ON = 'AimActedOn',
  CAPITAL_RAISE_INVESTOR_MATCH = 'CapitalRaiseInvestorMatch',
  CAP_TABLE_LABELLING = 'CapTableLabelling',
  MARKET_NEWS_INVESTOR_MATCHER = 'MarketNewsInvestorMatcher',
  BANK_TRANSACTION_MAPPING = 'BankTransactionMapping',
  INVENTORY_TYPE_MAPPING = 'InventoryTypeMapping',
  ENTITY_DUPLICATE_VERIFICATION = 'EntityDuplicateVerification',
  WIRE_ENTITY_MAPPING = 'WireEntityMapping',
  ALIAS_ENTITY_MAPPING = 'AliasEntityMapping',
  MARKET_NEWS_CUSTOMER_MATCHER = 'MarketNewsCustomerMatcher',
  EMAIL_TRIAGE = 'EmailTriage',
  ERROR_TRIAGE = 'ErrorTriage',
  EMAIL_TRIAGE_SCHEDULING_DRAFT = 'EmailTriageSchedulingDraft',
  CHANNEL_TRIAGE = 'ChannelTriage',
  // Classifies AIM conversations for a create/open-task prompt, and drafts the task.
  TASK_SUGGESTION = 'TaskSuggestion',
  WATCH_EVALUATION = 'WatchEvaluation',
  DRAFT_SCHEDULING_EMAIL = 'DraftSchedulingEmail',
  CRM_DOSSIER = 'CrmDossier',
  CRM_CATCH_UP = 'CrmCatchUp',
  COMPANY_DESCRIPTION = 'CompanyDescription',
  COMPANY_SHORT_DESCRIPTION = 'CompanyShortDescription',
  MARKET_NEWS_HEADLINES = 'MarketNewsHeadlines',
  VOICE_TONE = 'VoiceTone',
  GMAIL_DRAFT_VOICE = 'GmailDraftVoice',
  NOTE_CELL_ATTRIBUTION = 'NoteCellAttribution',
  NOTE_CELL_SEGMENTATION = 'NoteCellSegmentation',
  DAILY_BRIEFING = 'DailyBriefing',
  PROJECT_STATUS_SUMMARY = 'ProjectStatusSummary',
  MEETING_BRIEF = 'MeetingBrief',
  MEETING_BRIEF_SCOPE = 'MeetingBriefScope',
  ARC_LIVE_NOTES = 'ArcLiveNotes',
  /** Whether Arc, listening in an AIM huddle, has one thing worth saying out loud. */
  HUDDLE_ARC_INSIGHT = 'HuddleArcInsight',
  /** An ended huddle's overview, takeaways and tasks, from its transcript. */
  HUDDLE_SUMMARY = 'HuddleSummary',
  AIM_BRIEF = 'AimBrief',
  INVENTORY_APPRAISAL_EXTRACTION = 'InventoryAppraisalExtraction',
  CHECK_REMITTANCE_EXTRACTION = 'CheckRemittanceExtraction',
  /** Picks which client a lockbox check pays, from the check image and the lockbox's clients. */
  CHECK_CLIENT_PREDICTION = 'CheckClientPrediction',
  PROMPT_DESCRIPTION = 'PromptDescription',
  /**
   * Maps every detail line of an uploaded income statement or balance sheet onto
   * a summary category, and tags the totals the client reported.
   */
  FINANCIALS_CATEGORY_PREDICTION = 'FinancialsCategoryPrediction',
  /**
   * Narrow second pass over a statement whose tie-out broke (`findBrokenChecks`), on only the
   * rows feeding that check; tracked apart for its cost and latency.
   */
  FINANCIALS_RECONCILIATION_REPAIR = 'FinancialsReconciliationRepair',
  /**
   * The Review's "why this month is off": prose written from a deterministic
   * diagnosis of the mapped lines, one call per check and month, cached until
   * the rows behind it change.
   */
  FINANCIALS_RECONCILIATION_EXPLANATION = 'FinancialsReconciliationExplanation',
  /** The agent that searches for the lines behind a difference the quick search can't explain. */
  FINANCIALS_RECONCILIATION_DIAGNOSIS = 'FinancialsReconciliationDiagnosis',
  /**
   * A subagent spawned by the main Arc chat agent via `spawn_subagent`. Each
   * subagent run logs its own AgentRun row (contextId = the parent session id)
   * so its cost/tokens are attributed distinctly from the parent AtlasAI run.
   */
  SUBAGENT = 'Subagent',
  /**
   * A headless Claude Code session run by the Agent SDK in a read-only checkout of this repo
   * (`services/codeAgent`). Investigates feedback, errors and tasks against the real source.
   */
  CODE_AGENT = 'CodeAgent',
  /** The synthesis call of the Atlas Product Board triage (`services/taskTriage`). */
  TASK_TRIAGE = 'TaskTriage',
  /** Names an Arc session or a team conversation from its opening messages. */
  CHAT_NAMING = 'ChatNaming',
  /** One recommendation per counterparty redline against accepted LSA precedent. */
  LEGAL_REDLINE_PREDICTION = 'LegalRedlinePrediction',
  /** Vector embeddings; kill switch only — `getEmbeddings` logs no `agent_run` row per batch. */
  EMBEDDINGS = 'Embeddings',
  /** Speech-to-text of voice memos (`common/audio/transcription.ts`). */
  TRANSCRIPTION = 'Transcription',
}

export interface ISqlQueryEntry {
  sql: string
  results?: Record<string, unknown>[]
  rowCount?: number
}

// Unified citation model — every factual claim in an AtlasAI response can cite a
// typed source via `[^<id>]` markers inline. `id` is short + agent-referencable
// (e.g. "sql-1", "doc-2", "note-3", "mem-1"). `metadata` is type-specific.
export type CitationType =
  | 'sql'
  | 'doc'
  | 'note'
  | 'memory'
  | 'prompt'
  | 'legal'
  | 'schema'
  | 'web'
  | 'service'
  | 'code'
  | 'chart'

export interface ICitation {
  id: string
  type: CitationType
  label: string
  subtitle?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- citation metadata varies by tool type; TypeORM's QueryDeepPartialEntity rejects Record<string, unknown>
  metadata: Record<string, any>
}

/**
 * Declarative spec for an inline chart. Emitted by the RENDER_CHART tool and
 * stored on a `chart` citation's `metadata.spec`. The client renders it with
 * chart.js — Arc only picks the chart type + which fields map to which axis,
 * it never produces drawing code. Data comes from exactly one of `dataRef`
 * (reuse the rows of an existing `sql-N` citation in the same turn — preferred,
 * avoids re-emitting data) or inline `data`.
 *
 * Dual y-axis: assign a series `yAxisID: 'y1'` to plot it against a second,
 * independently-scaled right-hand axis (e.g. pass-throughs alongside fundings).
 * Series default to the left axis (`'y'`). Cartesian charts only
 * (bar/line/area/scatter); ignored by pie/doughnut.
 */
export interface IChartSpec {
  type: 'bar' | 'line' | 'area' | 'pie' | 'doughnut' | 'scatter'
  title?: string
  /** Citation id (e.g. "sql-1") whose `metadata.rows` supply the chart's data. */
  dataRef?: string
  /** Inline rows, used when no `dataRef` is given. */
  data?: Record<string, unknown>[]
  /** Field used for category labels / x-axis (or slice labels for pie/doughnut). */
  x: string
  /**
   * Numeric field(s) → one dataset each. pie/doughnut use only `series[0]`.
   * `yAxisID` selects which y-axis the series plots against — `'y'` (left,
   * default) or `'y1'` (right). Omit for single-axis charts.
   */
  series: { field: string; label?: string; yAxisID?: 'y' | 'y1' }[]
  /** Stack bar/area datasets on a shared baseline. */
  stacked?: boolean
  /** Title for the left y-axis (`'y'`). */
  yLabel?: string
  /** Title for the right y-axis (`'y1'`). Only shown when a series uses `yAxisID: 'y1'`. */
  y1Label?: string
}

/**
 * Citation type subset that a memory can point back to as its source. A memory
 * is never derived from a `web` or `service` citation (those are transient,
 * not knowledge-bearing), so we exclude them here.
 */
export type MemorySourceType = Exclude<CitationType, 'web' | 'service' | 'code'>

/**
 * Structured pointer back to a source that produced a memory. Used to build
 * provenance trails ("this memory came from citation doc-2 in session XYZ,
 * which pointed at atlas_docs[credit-policy#concentration-limits]").
 */
export interface IMemorySourceRef {
  type: MemorySourceType
  /** Stable doc identity (preferred) */
  docSlug?: string
  sectionId?: string
  /** Fallback identity when stable refs aren't available */
  docId?: string
  memoryKey?: string
  /** Human-readable label for UI rendering */
  label?: string
  /** The citation id the source had in its originating turn (e.g. "doc-2") */
  citeId?: string
}

/**
 * A section derived from a doc's markdown heading structure. `id` is stable
 * across edits as long as the heading title at that level doesn't change
 * (hash of doc_slug + heading path).
 */
export interface IAtlasDocSection {
  id: string
  /** 1 = H1, 2 = H2, 3 = H3 */
  level: number
  title: string
  /** ['H1 title', 'H2 title'] for an H2 section under an H1 */
  path: string[]
  /** character offset in content where this section begins */
  charStart: number
  /** character offset where this section ends (exclusive) */
  charEnd: number
}

/**
 * A resolved outbound link extracted from a doc's markdown content.
 * Both standard markdown links and [[wiki-links]] are represented here.
 */
export interface IOutboundLink {
  /** Target doc's stable slug (null if the link couldn't resolve to a known doc) */
  targetSlug: string | null
  /** Target section within the doc (null if linking to the whole doc) */
  targetSectionId: string | null
  /** The display text the author wrote */
  display: string
  /** Whether the link resolved to an existing doc at save time */
  resolved: boolean
  /** Raw syntax as-written for diagnostics */
  raw: string
}

export interface IChatMessageAttachment {
  /** Set for a chat-dropzone upload, which lives in Box. */
  boxFileId?: string
  /**
   * Set for a file attached to the channel message that @-mentioned Arc. Those
   * live in S3 and have no Box id at all, so the two locators are alternatives —
   * exactly one is present.
   */
  channelAttachmentId?: string
  name: string
  mimeType: string
  sizeBytes: number
  preparedAs: 'document' | 'image' | 'container_upload' | 'inline_text'
  /** Optional Box deep link, present on freshly uploaded Box attachments. */
  url?: string
}

export interface IChatMessageOutputFile {
  boxFileId: string
  name: string
  url: string
  /** `wrote` = UploadBoxFile (new file), `updated` = UpdateBoxFile (new version of an existing file). */
  action: 'wrote' | 'updated'
}

export interface IChatMessage {
  id: string
  role: ChatRole
  content: string
  contentState?: string // JSON string of Draft.js RawDraftContentState for rich text rendering
  sqlQuery?: string
  queryResults?: string
  sqlQueries?: ISqlQueryEntry[]
  sessionId?: string
  feedbackType?: Feedback
  feedbackComment?: string
  timeToRespondInMs?: number
  createdAt?: string
  /**
   * Sender info hydrated on user-role messages in group chats. Solo sessions
   * leave these undefined so existing single-user UI is unchanged.
   */
  senderUserId?: string
  senderName?: string
  senderAvatar?: string | null
  /**
   * True on a system-injected user turn (a one-shot watch firing/expiring
   * resumes the chat). The client hides this user bubble so only Arc's response
   * shows and the turn reads as Arc proactively reaching out. Only ever set on
   * USER-role messages; undefined/false on normal user-typed turns.
   */
  hiddenQuestion?: boolean
  relevantNotes?: Array<{
    content: string
    metadata: Record<string, unknown>
    date: string
    username: string
    clientName: string
    contextScore: number
    contextType: string
  }>
  mentions?: IAtlasAIMention[]
  citations?: ICitation[]
  /** Per-turn reasoning captured while the agent ran — rendered under Sources. */
  reasoning?: IAtlasAITurnText[]
  /** Files the user attached at chat-input time — rendered alongside the user message. */
  attachments?: IChatMessageAttachment[]
  /** Files Arc CREATED or UPDATED in this turn — rendered as chips on the assistant bubble. */
  outputFiles?: IChatMessageOutputFile[] | null
  /**
   * Server-set flag. True when the assistant's reply used file-delivery
   * language but no successful Box write tool ran — frontend renders a yellow
   * warning Alert. Undefined/null/false on clean replies and on legacy rows.
   */
  unverifiedFileClaim?: boolean | null
  status?: AtlasAIMessageStatus
  /**
   * When status === COMPLETED_WITH_ISSUES, holds the raw error message from
   * the late-stage stream failure (e.g., "Claude stream aborted",
   * "AGENT_ABORTED"). UI shows the response normally but exposes this on
   * hover/click so users understand the warning indicator.
   */
  errorDetails?: string | null
  /**
   * Latest "status" string emitted while the agent was working (e.g.
   * "Querying database…", "Calling listClients…"). Persisted on every
   * ATLAS_AI_THINKING websocket emit so a refresh mid-run can rehydrate the
   * processing card without losing context.
   */
  progressText?: string | null
  /** Terminal-status approvals queued by this assistant message — rendered inline. */
  approvals?: IMessageApproval[]
  /**
   * input_tokens of this turn's final API request — how much of the model's
   * context window the conversation occupied at this point. Drives the subtle
   * context-usage gauge in the chat UI (value ÷ getUsableBudgetTokens(model)).
   * Null/undefined on legacy rows and on turns that didn't run the tool agent.
   */
  contextTokens?: number | null
  /**
   * The model that actually produced this response — NOT necessarily the one
   * selected for the session. When a provider is down, the server re-serves the
   * turn on a healthy model rather than failing it. Null on rows written before
   * per-turn attribution existed.
   */
  modelUsed?: Models | null
  /**
   * The model the user asked for, set ONLY when failover replaced it. Non-null
   * is the signal to render the "answered with X instead" note — an answer that
   * came from a different model than the picker shows must never be attributed
   * silently. Null means the chosen model served the turn normally.
   */
  modelFallbackFrom?: Models | null
}

export interface ICurrentResponse {
  response: string
  sqlQuery: string
  queryResults?: string
  sessionId: string
  question: string
  processingTimeMs: number
}

export interface IAtlasAITurnText {
  turnIndex: number
  text: string
  toolsCalled: string[]
  stopReason: string | null
}

/**
 * A concrete write service that Arc actually executed during a turn. Unlike the
 * coarse `toolsCalled` (every service call shows up as `execute_service`), this
 * carries the real service name (e.g. `AddCommentaryReply`, `CreateAtlasTask`)
 * plus its tier, so the client can refetch exactly the affected page data and
 * ignore external-only actions (Asana/Gmail/Calendar). Emitted on
 * ATLAS_AI_COMPLETE; write tiers only, deduped by service.
 */
export interface IArcExecutedService {
  service: string
  tier: string
}

export interface IChatHistory {
  hasMore: boolean
  messages: IChatMessage[]
  total: number
  limit: number
  offset: number
  thinking?: string
  result?: unknown
  /**
   * Per-turn text emitted while Arc is working. Accumulates for the duration
   * of the current ask; cleared when the next ASK_ATLAS_AI_REQUEST fires.
   * Shows intermediate reasoning in the chat UI and lets us see text that
   * would otherwise be discarded (the final response only keeps the last
   * turn's text).
   */
  turnTexts?: IAtlasAITurnText[]
}

export interface IChatSessionAutomation {
  id: string
  name: string
  triggerType: ArcAutomationTriggerType
  /** Null for event-triggered automations. */
  cronExpression: string | null
  /** Selected objects for event-triggered automations; null for scheduled ones. */
  eventObjects: string[] | null
  /** Lifecycle status for event-triggered automations; null for scheduled ones. */
  eventStatus: string | null
  /** False for a one-time automation that fires once then stops. Drives whether
   * the sidebar shows a recurring cadence or a single dated event. */
  recurring: boolean
  /** True for an invisible chat-tied watch or a one-time scheduled run. */
  oneShot: boolean
  /** The next scheduled fire time (or, for a one-shot, its single fire time) as
   * an ISO string. Null for event triggers, which have no clock. */
  nextRunAt: string | null
}

export enum ChatSessionMode {
  SOLO = 'solo',
  GROUP = 'group',
}

export interface IChatSessionMember {
  userId: string
  name: string
  avatar: string | null
  role: 'owner' | 'member'
  joinedAt: string
}

export interface IChatSession {
  id: string
  name: string
  /**
   * Model this chat last ran a turn on. Sent by Get (session payload) so
   * reopening a chat restores its own model in the picker instead of whichever
   * model was selected most recently in another chat. Absent on the sidebar
   * list payload, which doesn't need it.
   */
  model?: Models
  lastActive: string
  lastMessage: string
  matchedPreview?: string
  history?: IChatHistory
  isUnread?: boolean
  isPinned?: boolean
  mode?: ChatSessionMode
  members?: IChatSessionMember[]
  automation?: IChatSessionAutomation | null
}

export interface IChatSessionsResponse {
  data: IChatSession[]
  automationFolders?: IAutomationFolder[]
  total: number
  limit: number
  offset: number
}

export interface IAutomationFolder {
  automationId: string
  automationName: string
  triggerType: ArcAutomationTriggerType
  /** Null for event-triggered automations. */
  cronExpression: string | null
  /** Selected objects for event-triggered automations; null for scheduled ones. */
  eventObjects: string[] | null
  /** Lifecycle status for event-triggered automations; null for scheduled ones. */
  eventStatus: string | null
  /** False for a one-time automation that fires once then stops. Drives whether
   * the sidebar shows a recurring cadence or a single dated event. */
  recurring: boolean
  /** True for an invisible chat-tied watch or a one-time scheduled run. */
  oneShot: boolean
  /** The next scheduled fire time (or, for a one-shot, its single fire time) as
   * an ISO string. Null for event triggers, which have no clock. */
  nextRunAt: string | null
  totalRuns: number
  latestSession: Pick<IChatSession, 'id' | 'name' | 'lastActive' | 'lastMessage' | 'isUnread'>
}

export enum Models {
  GPT_5_NANO = 'gpt-5-nano',
  GPT_5_MINI = 'gpt-5-mini',
  GPT_5_2 = 'gpt-5.2',
  /** Retired as a chat option by GPT-6 Astra; kept for stored picks and past runs. */
  GPT_5_6_SOL = 'gpt-5.6-sol',
  GPT_6_ASTRA = 'gpt-6-astra',
  GROK_4_6 = 'grok-4.6',
  /** Retired as a chat option by Claude Sonnet 5.5; kept for stored picks and past runs. */
  CLAUDE_SONNET_5 = 'claude-sonnet-5',
  CLAUDE_SONNET_5_5 = 'claude-sonnet-5-5',
  /** Retired as a chat option by Claude Opus 5.5; kept for stored picks and past runs. */
  CLAUDE_OPUS_5 = 'claude-opus-5',
  CLAUDE_OPUS_5_5 = 'claude-opus-5-5',
  /** Retired as a chat option by Claude Haiku 5.5; kept for stored picks and past runs. */
  CLAUDE_HAIKU_4_5 = 'claude-haiku-4-5',
  CLAUDE_HAIKU_5_5 = 'claude-haiku-5-5',
}

export enum ModelProvider {
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
  GOOGLE = 'google',
  XAI = 'xai',
}

/**
 * How a model's provider-executed ("server-side") tools are expressed on the
 * wire. Anthropic sends them as `code_execution_20250825` /
 * `web_search_20250305` tool configs and returns dedicated result block types;
 * OpenAI and xAI both expose them through the Responses API as
 * `code_interpreter` / `web_search` tools. The adapter keys off this to decide
 * which dialect to emit and how to translate results back into the canonical
 * (Anthropic-shaped) block format.
 */
export enum ServerToolDialect {
  ANTHROPIC = 'anthropic',
  OPENAI = 'openai',
}

/**
 * Tokens reserved for the model's own output. The input prompt must leave room
 * for the response, so it can never occupy the full window. Sized above the
 * largest configured `maxTokens` for an Arc run (~25k) with margin.
 */
export const CONTEXT_OUTPUT_RESERVE_TOKENS = 32_000

/**
 * Tokens held back as headroom for a turn's tool/SQL-result accumulation. This
 * is the part of a prompt that actually overflows the window: Arc replays a
 * history bounded by HISTORY_REPLAY_BUDGET_TOKENS, but a single tool-heavy turn
 * can append far more. Prod (60d, 2.7k AtlasAI runs) puts per-run tool output at
 * p90 ≈ 43k, p95 ≈ 71k, p99 ≈ 337k tokens — so we reserve a buffer between the
 * common and worst case to warn before a heavy next turn tips over the ceiling.
 */
export const CONTEXT_TOOL_GROWTH_BUFFER_TOKENS = 118_000

/**
 * Usable input budget = the slice of the context window an Arc prompt may
 * safely occupy before the next turn risks a "prompt is too long" 400. Drives
 * BOTH the chat UI's context-usage gauge (tokens ÷ this) and the server's
 * pre-flight guard, so what the gauge shows as "full" is exactly what the
 * server refuses.
 *
 * Per-model, because the window is: Claude runs at 1M (− 32,000 − 118,000 =
 * 850,000), GPT-6 Astra at 1.05M, Grok 4.6 at 500k. The reserves themselves are
 * absolute — they bound output size and a turn's tool-result growth, neither
 * of which scales with the window.
 *
 * Declared as a `function` (not a const arrow) so it hoists above
 * MODEL_CAPABILITIES, which is defined further down this file.
 */
export function getUsableBudgetTokens(model: Models): number {
  const window = MODEL_CAPABILITIES[model].contextWindowTokens
  return window - CONTEXT_OUTPUT_RESERVE_TOKENS - CONTEXT_TOOL_GROWTH_BUFFER_TOKENS
}

/** Rough chars-per-token ratio for prompt-size estimates (English + code ≈ 4).
 * The single conversion used by both the replay window and the client gauge. */
export const CHARS_PER_TOKEN_ESTIMATE = 4

/**
 * Token budget for the conversation history Arc replays into a turn — the size
 * of the rolling window, enforced by `selectHistoryWindow`
 * (common/helpers/atlasaiHistoryWindow.ts) on both the server and the client
 * gauge.
 *
 * A token budget, not a turn count: ten turns is 2k tokens or 500k tokens
 * depending on the conversation, so counting turns bounds the wrong quantity.
 * Whole turns are kept newest-first until this is spent; older turns are
 * evicted into a synopsis rather than silently dropped.
 *
 * Sized to leave the tool-growth headroom the in-run compaction needs:
 * 200,000 + SYSTEM_PROMPT_TOKENS_ESTIMATE (12,000) = 212,000 projected, against
 * a CONTEXT_USABLE_BUDGET_TOKENS of 850,000 — so a turn can still accumulate
 * ~638k tokens of tool output before it risks the window.
 */
export const HISTORY_REPLAY_BUDGET_TOKENS = 200_000

/**
 * Per-message ceiling applied to a replayed history message BEFORE it is
 * charged against HISTORY_REPLAY_BUDGET_TOKENS, so one table-heavy answer can't
 * evict the rest of the conversation. ~10k tokens; truncation keeps a head and
 * a tail with an explicit marker (see `truncateReplayMessage`) rather than
 * cutting mid-sentence and leaving the model to assume it saw the whole thing.
 */
export const HISTORY_MESSAGE_MAX_CHARS = 40_000

/** Head/tail split of HISTORY_MESSAGE_MAX_CHARS. Head-weighted: an answer's
 * setup and figures lead, but the conclusion is usually last, so keep both. */
export const HISTORY_MESSAGE_HEAD_CHARS = 30_000
export const HISTORY_MESSAGE_TAIL_CHARS = 10_000

/** Fixed overhead (system prompt, tool schemas, page context) added to the
 * client pre-send estimate. Approximate — the gauge is ambient, and the
 * server's measured count is authoritative once a turn completes. */
export const SYSTEM_PROMPT_TOKENS_ESTIMATE = 12_000

/** Reasoning-depth levels accepted by Anthropic's `output_config.effort`. */
export type ModelEffort = 'low' | 'medium' | 'high' | 'xhigh' | 'max'

/**
 * Everything that varies between the models Arc can run, in one row per model.
 *
 * This is the single source of truth the rest of the stack derives from — the
 * picker list, cost telemetry, the context gauge and its server-side guard, and
 * which wire dialect the agent loop speaks. Adding a model is one row here plus
 * an API key in `config.llms`; nothing else needs to change.
 */
export interface IModelCapabilities {
  /** Display name in the model picker. */
  label: string
  provider: ModelProvider
  /**
   * Total context window. Denominator for the chat UI's context-usage gauge
   * (via getUsableBudgetTokens) and the server's pre-flight overflow guard.
   */
  contextWindowTokens: number
  /** Per-million-token list price (USD). */
  pricing: { input: number; output: number }
  /**
   * Multipliers applied to the base INPUT rate for prompt-cache traffic.
   *
   * These are provider economics, not a shared constant, and they vary by model
   * as well as by provider: Anthropic bills a cache WRITE at 1.25x input and a
   * read at 0.10x; OpenAI doesn't report writes separately, so they land in
   * ordinary input and carry 1.0x here; xAI doesn't bill writes at all. Reads
   * are 90% off on OpenAI and Anthropic, 75% off on xAI. Hardcoding one
   * provider's numbers would misprice every run on the others.
   */
  cacheMultipliers: { write: number; read: number }
  /**
   * Some providers switch to a higher rate once a single request's prompt
   * exceeds `thresholdTokens` (a prompt of exactly that size still pays the
   * standard rate), which for a long Arc conversation is the common case rather
   * than the exception — GPT-6 Astra roughly doubles above 272k, Grok 4.6
   * doubles above 200k, Haiku 5.5 is 5x above 100k. Omitted where there is no cliff.
   */
  longContextPricing?: { thresholdTokens: number; input: number; output: number }
  /**
   * Dialect for provider-executed code execution, or null when the model has
   * no sandbox. Drives whether buildTools appends a code-execution tool at all.
   */
  serverCodeExecution: ServerToolDialect | null
  /** Dialect for provider-hosted file input (PDF/image/sandbox mounts). */
  serverFiles: ServerToolDialect | null
  /**
   * Whether the model supports EXPLICIT prompt-cache breakpoints
   * (Anthropic `cache_control`). OpenAI and xAI cache automatically with no
   * markers, so the agent loop skips its rolling-breakpoint pass for them.
   */
  promptCaching: boolean
  /** Whether the model can be constrained to a JSON schema. */
  structuredOutput: boolean
  /**
   * Whether the model accepts a FORCED `tool_choice` (`any` / `tool`). Claude
   * Opus 5.5 and Sonnet 5.5 reject both with a 400 (Haiku 5.5 accepts them);
   * `auto` and `none` are unaffected everywhere. False means the provider adapter downgrades a forced choice to
   * the model's default, so the caller's instruction has to carry the intent —
   * see AtlasAi's file-output hallucination retry, whose corrective user
   * message names the tools it wants called.
   */
  forcedToolChoice: boolean
  /**
   * `output_config.effort` sent on every Anthropic request for this model, or
   * null when the parameter is rejected (Haiku 4.5) or the provider has no
   * equivalent (the OpenAI/xAI adapter sends none). Stated per row rather than
   * left to the provider because the provider default MOVES: Claude Opus 5 ran
   * an unspecified request at `high`, Opus 5.5 runs it at `medium`, so a swap
   * that says nothing silently drops a reasoning tier.
   */
  effort: ModelEffort | null
  /** Key in `config.llms` that must be non-empty for this model to run. */
  configKey: string
  /** Whether the model is offered in the user-facing picker. */
  selectable: boolean
  /**
   * Preference order when this catalogue is searched for a model to RESCUE a
   * turn whose own provider is down. Lower wins.
   *
   * Ranked by capability, not by price. A rescue is already the unhappy path —
   * the user asked for one model and is getting another — so the axis that
   * matters is answering well, not answering cheaply. Ranking on the cheapest
   * input rate (the previous tiebreak) made Haiku the automatic second hop for
   * every origin, so someone who chose Opus and hit an outage was quietly
   * answered by the weakest model on the board.
   *
   * Haiku is deliberately last: it is the right model for a cheap classification
   * call and the wrong one to silently substitute into an Opus conversation.
   * Non-selectable rows are ranked past every selectable one; they never serve
   * agent turns and the chain filters them out anyway.
   */
  fallbackRank: number
}

/**
 * Pricing and context windows verified against provider pricing pages,
 * 2026-07 (GPT-6 Astra 2026-09-03, Claude Opus 5.5 2026-09-22, Claude Sonnet 5
 * and 5.5 2026-09-28, Claude Haiku 4.5 and 5.5 2026-10-07). Claude cache
 * multipliers per anthropic.com/docs/prompt-caching.
 */
export const MODEL_CAPABILITIES: Record<Models, IModelCapabilities> = {
  [Models.CLAUDE_SONNET_5_5]: {
    label: 'Claude Sonnet 5.5',
    provider: ModelProvider.ANTHROPIC,
    contextWindowTokens: 1_000_000,
    pricing: { input: 2, output: 10 },
    cacheMultipliers: { write: 1.25, read: 0.1 },
    serverCodeExecution: ServerToolDialect.ANTHROPIC,
    serverFiles: ServerToolDialect.ANTHROPIC,
    promptCaching: true,
    structuredOutput: true,
    forcedToolChoice: false,
    effort: 'high',
    configKey: 'anthropicApiKey',
    selectable: true,
    fallbackRank: 2,
  },
  [Models.CLAUDE_OPUS_5_5]: {
    label: 'Claude Opus 5.5',
    provider: ModelProvider.ANTHROPIC,
    contextWindowTokens: 1_000_000,
    pricing: { input: 4, output: 20 },
    // Anthropic prices a cache read on THIS model at 0.05x input ($0.20/MTok),
    // half the 0.1x every other Claude row pays. Copying the sibling rows would
    // double-count every cached token Arc reads.
    cacheMultipliers: { write: 1.25, read: 0.05 },
    serverCodeExecution: ServerToolDialect.ANTHROPIC,
    serverFiles: ServerToolDialect.ANTHROPIC,
    promptCaching: true,
    structuredOutput: true,
    forcedToolChoice: false,
    effort: 'high',
    configKey: 'anthropicApiKey',
    selectable: true,
    fallbackRank: 1,
  },
  // Retired as a chat option when Claude Opus 5.5 replaced it, but kept whole,
  // for the same reason as gpt-5.6-sol below: anything written between deploy
  // and the migration still resolves a valid row and runs at its real price.
  [Models.CLAUDE_OPUS_5]: {
    label: 'Claude Opus 5',
    provider: ModelProvider.ANTHROPIC,
    contextWindowTokens: 1_000_000,
    pricing: { input: 5, output: 25 },
    cacheMultipliers: { write: 1.25, read: 0.1 },
    serverCodeExecution: ServerToolDialect.ANTHROPIC,
    serverFiles: ServerToolDialect.ANTHROPIC,
    promptCaching: true,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: 'high',
    configKey: 'anthropicApiKey',
    selectable: false,
    fallbackRank: 94,
  },
  // Retired as a chat option when Claude Sonnet 5.5 replaced it; kept whole for
  // the same reason as Opus 5 above. Priced as published — the same rates as 5.5.
  [Models.CLAUDE_SONNET_5]: {
    label: 'Claude Sonnet 5',
    provider: ModelProvider.ANTHROPIC,
    contextWindowTokens: 1_000_000,
    pricing: { input: 2, output: 10 },
    cacheMultipliers: { write: 1.25, read: 0.1 },
    serverCodeExecution: ServerToolDialect.ANTHROPIC,
    serverFiles: ServerToolDialect.ANTHROPIC,
    promptCaching: true,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: 'high',
    configKey: 'anthropicApiKey',
    selectable: false,
    fallbackRank: 95,
  },
  [Models.CLAUDE_HAIKU_5_5]: {
    label: 'Claude Haiku 5.5',
    provider: ModelProvider.ANTHROPIC,
    contextWindowTokens: 1_000_000,
    pricing: { input: 0.1, output: 0.5 },
    cacheMultipliers: { write: 1.25, read: 0.1 },
    longContextPricing: { thresholdTokens: 100_000, input: 0.5, output: 2.5 },
    serverCodeExecution: ServerToolDialect.ANTHROPIC,
    serverFiles: ServerToolDialect.ANTHROPIC,
    promptCaching: true,
    structuredOutput: true,
    forcedToolChoice: true,
    // Thinking is on by default and counts against `max_tokens`. Haiku 4.5 ran
    // without it, and the cheap-tier callers size their caps (256-2048) for that.
    effort: 'low',
    configKey: 'anthropicApiKey',
    selectable: true,
    fallbackRank: 5,
  },
  // Retired as a chat option when Claude Haiku 5.5 replaced it; kept whole for
  // the same reason as Opus 5 above.
  [Models.CLAUDE_HAIKU_4_5]: {
    label: 'Claude Haiku 4.5',
    provider: ModelProvider.ANTHROPIC,
    contextWindowTokens: 200_000,
    pricing: { input: 1, output: 5 },
    cacheMultipliers: { write: 1.25, read: 0.1 },
    serverCodeExecution: ServerToolDialect.ANTHROPIC,
    serverFiles: ServerToolDialect.ANTHROPIC,
    promptCaching: true,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: null,
    configKey: 'anthropicApiKey',
    selectable: false,
    fallbackRank: 96,
  },
  [Models.GPT_6_ASTRA]: {
    label: 'ChatGPT-6 Astra',
    provider: ModelProvider.OPENAI,
    // Matches Claude's window, so the history-replay budget has the same
    // headroom here as it does on Anthropic.
    contextWindowTokens: 1_050_000,
    pricing: { input: 10, output: 50 },
    // OpenAI bills Astra cache writes at 1.25x input, but the Responses usage
    // payload doesn't separate them from ordinary input — they arrive inside
    // input_tokens and are priced at 1.0x. A bounded under-report, not a guess.
    // Reads stay 90% off ($1.00 on a $10 rate).
    cacheMultipliers: { write: 1, read: 0.1 },
    longContextPricing: { thresholdTokens: 272_000, input: 20, output: 75 },
    serverCodeExecution: ServerToolDialect.OPENAI,
    serverFiles: ServerToolDialect.OPENAI,
    promptCaching: false,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: null,
    configKey: 'openAIApiKey',
    selectable: true,
    fallbackRank: 3,
  },
  // Retired as a chat option when GPT-6 Astra replaced it, but kept whole: a
  // session, channel or automation whose stored pick hasn't been migrated still
  // resolves a valid row and keeps running at its real price, and cost telemetry
  // for past runs stays accurate. Same treatment as gpt-5.2 below.
  [Models.GPT_5_6_SOL]: {
    label: 'ChatGPT-5.6',
    provider: ModelProvider.OPENAI,
    contextWindowTokens: 1_050_000,
    pricing: { input: 5, output: 30 },
    // OpenAI didn't bill cache writes on this model; reads are 90% off.
    cacheMultipliers: { write: 1, read: 0.1 },
    longContextPricing: { thresholdTokens: 272_000, input: 10, output: 45 },
    serverCodeExecution: ServerToolDialect.OPENAI,
    serverFiles: ServerToolDialect.OPENAI,
    promptCaching: false,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: null,
    configKey: 'openAIApiKey',
    selectable: false,
    fallbackRank: 93,
  },
  // Not a chat option: this id is how the codebase names "the OpenAI provider"
  // when asking for embeddings (getEmbeddings routes on it), including in a
  // seeded migration — so the enum member has to stay even though no agent turn
  // should run on it.
  [Models.GPT_5_2]: {
    label: 'ChatGPT-5.2',
    provider: ModelProvider.OPENAI,
    contextWindowTokens: 400_000,
    pricing: { input: 0.875, output: 7 },
    cacheMultipliers: { write: 1, read: 0.1 },
    serverCodeExecution: ServerToolDialect.OPENAI,
    serverFiles: ServerToolDialect.OPENAI,
    promptCaching: false,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: null,
    configKey: 'openAIApiKey',
    selectable: false,
    fallbackRank: 90,
  },
  [Models.GROK_4_6]: {
    label: 'Grok 4.6',
    provider: ModelProvider.XAI,
    contextWindowTokens: 500_000,
    pricing: { input: 2, output: 6 },
    // xAI doesn't bill cache writes; cached input is 75% off.
    cacheMultipliers: { write: 1, read: 0.25 },
    longContextPricing: { thresholdTokens: 200_000, input: 4, output: 12 },
    serverCodeExecution: ServerToolDialect.OPENAI,
    serverFiles: ServerToolDialect.OPENAI,
    promptCaching: false,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: null,
    configKey: 'xaiApiKey',
    selectable: true,
    fallbackRank: 4,
  },
  // Cheap OpenAI models used for embeddings//sensor-style work, never offered
  // in the chat picker. Pricing is nominal — they don't run agent turns.
  [Models.GPT_5_NANO]: {
    label: 'GPT-5 nano',
    provider: ModelProvider.OPENAI,
    contextWindowTokens: 400_000,
    pricing: { input: 0.05, output: 0.4 },
    cacheMultipliers: { write: 1, read: 0.1 },
    serverCodeExecution: null,
    serverFiles: null,
    promptCaching: false,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: null,
    configKey: 'openAIApiKey',
    selectable: false,
    fallbackRank: 91,
  },
  [Models.GPT_5_MINI]: {
    label: 'GPT-5 mini',
    provider: ModelProvider.OPENAI,
    contextWindowTokens: 400_000,
    pricing: { input: 0.25, output: 2 },
    cacheMultipliers: { write: 1, read: 0.1 },
    serverCodeExecution: null,
    serverFiles: null,
    promptCaching: false,
    structuredOutput: true,
    forcedToolChoice: true,
    effort: null,
    configKey: 'openAIApiKey',
    selectable: false,
    fallbackRank: 92,
  },
}

/**
 * Cost tiers. Call sites name a tier, never a model id, so a model release is a
 * one-line bump in PROVIDER_MODEL_TIERS (plus a capability row above). A caller
 * that deliberately runs above or below the tier its work suggests says why next
 * to its pick.
 *  - cheap: high-volume classification, extraction, routing, background sensor turns.
 *  - balanced: Arc chat and most generation, the everyday default.
 *  - premium: long, hard synthesis where quality outweighs cost.
 */
export type ModelTier = 'cheap' | 'balanced' | 'premium'

/**
 * The current model at each tier, for every provider with a catalogue row. A
 * provider with fewer models than tiers repeats its best fit.
 */
export const PROVIDER_MODEL_TIERS = {
  [ModelProvider.ANTHROPIC]: {
    cheap: Models.CLAUDE_HAIKU_5_5,
    balanced: Models.CLAUDE_SONNET_5_5,
    premium: Models.CLAUDE_OPUS_5_5,
  },
  [ModelProvider.OPENAI]: {
    cheap: Models.GPT_5_MINI,
    balanced: Models.GPT_6_ASTRA,
    premium: Models.GPT_6_ASTRA,
  },
  [ModelProvider.XAI]: {
    cheap: Models.GROK_4_6,
    balanced: Models.GROK_4_6,
    premium: Models.GROK_4_6,
  },
} as const satisfies Partial<Record<ModelProvider, Record<ModelTier, Models>>>

/**
 * xAI's realtime speech-to-speech model behind Arc Voice. Kept beside the chat
 * catalogue so a model bump still lands in this file, but outside it: a voice model
 * is billed per minute and served over a different API.
 */
export const XAI_VOICE_MODEL = 'grok-voice-latest'

/** xAI's speech-to-text model behind Arc Voice's live captions of the caller. */
export const XAI_TRANSCRIPTION_MODEL = 'grok-transcribe'

/** OpenAI's speech-to-text model behind voice memo transcription. */
export const OPENAI_TRANSCRIPTION_MODEL = 'whisper-1'

/** Whisper's list price, for the `agent_runs` cost of a transcription. */
export const OPENAI_TRANSCRIPTION_USD_PER_MINUTE = 0.006

/** The provider that server-side work and new Arc chats run on unless a caller picks another. */
export const DEFAULT_MODEL_PROVIDER = ModelProvider.ANTHROPIC

/** Tiers on the default provider, which is what call sites pick from. */
export const MODEL_TIERS = PROVIDER_MODEL_TIERS[DEFAULT_MODEL_PROVIDER]

/** The model an Arc chat turn uses when the caller doesn't pick one. */
export const DEFAULT_CHAT_MODEL = MODEL_TIERS.balanced

/** Convenience lookup kept for call sites that only need the provider. */
export const MODEL_TO_PROVIDER: Record<Models, ModelProvider> = Object.fromEntries(
  Object.entries(MODEL_CAPABILITIES).map(([model, caps]) => [model, caps.provider]),
) as Record<Models, ModelProvider>

/**
 * The user-facing model picker list, derived from the capability table so a new
 * model shows up by adding a row rather than editing this array. Ordered as
 * declared above (Claude first, then the other providers).
 */
export const MODEL_PROVIDERS: { name: string; value: Models }[] = (
  Object.entries(MODEL_CAPABILITIES) as [Models, IModelCapabilities][]
)
  .filter(([, caps]) => caps.selectable)
  .map(([value, caps]) => ({ name: caps.label, value }))

/** Every model a user is allowed to select — the LIVR `one_of` allowlist. */
export const SELECTABLE_MODELS: Models[] = MODEL_PROVIDERS.map(({ value }) => value)

/**
 * Canonical tool names for Atlas AI. Used by tool definitions (the `name` field
 * sent to the Anthropic API), citation capture, websocket status messages,
 * TOOL_BUILDERS, and anywhere a tool is referenced by name.
 *
 * RULE: never use a raw string for a tool name — always reference this enum.
 */
export enum AtlasAITool {
  SEARCH_SCHEMA = 'search_schema',
  EXECUTE_SQL = 'execute_sql',
  READ_MEMORY = 'read_memory',
  SEARCH_MEMORY = 'search_memory',
  WRITE_MEMORY = 'write_memory',
  SEARCH_LEGAL = 'search_legal',
  SEARCH_NOTES = 'search_notes',
  SEARCH_DOCS = 'search_docs',
  /**
   * Read-only codebase tools — let Arc trace and explain how the portal works
   * by reading its OWN source from GitHub. SEARCH_CODE locates a symbol/file,
   * LIST_CODE_TREE navigates directories, READ_CODE returns line-anchored
   * content (the only one that produces a citation). Internal users only —
   * inherited from AtlasAi's userRoleGroups.INTERNAL gate.
   */
  SEARCH_CODE = 'search_code',
  READ_CODE = 'read_code',
  LIST_CODE_TREE = 'list_code_tree',
  /**
   * Hand a multi-file "why does the portal do X / where does this come from" question to a
   * headless coding agent that works in a real checkout of the repo (grep, git history,
   * read-only SQL) and reports back with `path:line` evidence. Detached like
   * `spawn_subagent`: returns immediately and resumes the session when the investigation
   * finishes. Parent-only.
   */
  INVESTIGATE_CODE = 'investigate_code',
  DRAFT_DOC = 'draft_doc',
  SALESFORCE = 'salesforce',
  BRAVE_SEARCH = 'brave_search',
  /**
   * Fetch ONE url's full page as clean markdown (or structured JSON against a
   * caller-supplied schema) via Firecrawl's hosted scrape API. The fetch/parse
   * step that pairs with BRAVE_SEARCH's discovery step: Brave returns a
   * snippet, which is useless for a JS-rendered page or a PDF; this returns the
   * rendered body. Single-url only — not a crawler.
   */
  FIRECRAWL_SCRAPE = 'firecrawl_scrape',
  SEARCH_SESSIONS = 'search_sessions',
  EXECUTE_SERVICE = 'execute_service',
  DESCRIBE_SERVICE = 'describe_service',
  READ_PROMPT = 'read_prompt',
  SEARCH_PROMPT = 'search_prompt',
  WRITE_PROMPT = 'write_prompt',
  /**
   * Run a workflow — a code-backed multi-step program registered as
   * `kind: 'workflow'` in `aiCallableRegistry`. Distinct from
   * READ_PROMPT/SEARCH_PROMPT/WRITE_PROMPT, which operate on user-authored
   * prompts stored in agent_memory.
   */
  EXECUTE_WORKFLOW = 'execute_workflow',
  /** Take the person Arc is working with to a page in Atlas, in their own browser. */
  OPEN_PAGE = 'open_page',
  /** Change the report on the asker's screen, in a report's own conversation. */
  REVISE_REPORT = 'revise_report',
  /**
   * Inspect a workflow (full description + params + tier) before running it
   * with EXECUTE_WORKFLOW — the workflow analogue of DESCRIBE_SERVICE. Keeps the
   * per-workflow detail out of the always-loaded execute_workflow description.
   */
  DESCRIBE_WORKFLOW = 'describe_workflow',
  /**
   * Render an inline chart from a declarative spec (see {@link IChartSpec}).
   * Emits a `chart` citation; the model places the returned `[^chart-N]` marker
   * inline in its final answer where the chart should appear. Prefer
   * `dataRef:"sql-N"` to reuse a query's rows rather than re-emitting data.
   */
  RENDER_CHART = 'render_chart',
  /**
   * Terminating tool. The model calls this exactly once when it has the final
   * reply for the user; the harness captures `text` as the answer and breaks
   * the loop. Eliminates the "trailing ack misclassified as answer" and
   * "real answer skipped because it followed a memory write" failure modes
   * that any heuristic-based final-turn picker is prone to.
   */
  SUBMIT_FINAL_ANSWER = 'submit_final_answer',
  /**
   * Delegate one or more self-contained subtasks to isolated subagent runs.
   * Each subagent gets the full Arc toolset EXCEPT this tool (no recursion),
   * its own fresh context + sandbox, and its own turn budget. Only the
   * subagent's final answer plus a compact reasoning trace comes back to the
   * parent as a single tool result — the parent's context (and the persisted
   * chat history) never ingests the subagent's intermediate tool traffic. Use
   * for parallelizable, self-contained chunks of a larger task to keep the
   * main thread's context clean.
   */
  SPAWN_SUBAGENT = 'spawn_subagent',
  /**
   * Report the live status (and finished results) of the subagents spawned in
   * this conversation. Read-only; parent-only (subagents never see it). Lets the
   * model poll a fan-out it launched without waiting to be woken.
   */
  CHECK_SUBAGENTS = 'check_subagents',
  /**
   * Steer a still-running subagent by dropping a message into its inbox; the
   * subagent reads it at its next turn boundary and adapts without losing
   * progress. Parent-only. Use when a follow-up changes or narrows a running
   * subagent's task.
   */
  REDIRECT_SUBAGENT = 'redirect_subagent',
  /**
   * Cancel a running subagent (e.g. it was launched on a wrong premise). Flips
   * the run to CANCELLED and tears down its in-flight work at the next turn
   * boundary. Parent-only.
   */
  CANCEL_SUBAGENT = 'cancel_subagent',
  /**
   * Terminating tool for a wake turn: end this turn WITHOUT posting anything to
   * the user. The model calls this when it was woken by a subagent completion
   * but isn't ready to answer yet (still waiting on other subagents). Parent-only
   * — a subagent must always submit_final_answer instead.
   */
  DEFER_RESPONSE = 'defer_response',
  /**
   * Create a persisted, resumable step list for a long, dependency-chained
   * task. The serial counterpart to `spawn_subagent`: use it when step N needs
   * step N-1's output, so a fan-out won't work and a timeout mid-chain would
   * otherwise lose everything done so far. Parent-only.
   */
  CREATE_PLAN = 'create_plan',
  /**
   * Checkpoint a plan step — the durability primitive. Marking a step done (or
   * blocked, or failed) writes it to the DB immediately, so work already
   * finished survives a timeout and the next turn resumes from there instead of
   * restarting. Parent-only.
   */
  UPDATE_PLAN_STEP = 'update_plan_step',
  /**
   * Read the active plan's steps, statuses, and completed-step results. The
   * same report injected on a plan resume, available on demand. Parent-only.
   */
  READ_PLAN = 'read_plan',
}

/**
 * Short human-readable labels for each tool. Used by the UI to render tool
 * chips in the Reasoning accordion (e.g. "Query" instead of "execute_sql").
 * Keep these noun-form and concise — they appear as small chips.
 */
export const ATLAS_AI_TOOL_LABELS: Record<AtlasAITool, string> = {
  [AtlasAITool.SEARCH_SCHEMA]: 'Schema',
  [AtlasAITool.EXECUTE_SQL]: 'Query',
  [AtlasAITool.READ_MEMORY]: 'Memory',
  [AtlasAITool.SEARCH_MEMORY]: 'Memory',
  [AtlasAITool.WRITE_MEMORY]: 'Memory',
  [AtlasAITool.SEARCH_LEGAL]: 'Legal',
  [AtlasAITool.SEARCH_NOTES]: 'Notes',
  [AtlasAITool.SEARCH_DOCS]: 'Docs',
  [AtlasAITool.SEARCH_CODE]: 'Code Search',
  [AtlasAITool.READ_CODE]: 'Code',
  [AtlasAITool.LIST_CODE_TREE]: 'Files',
  [AtlasAITool.INVESTIGATE_CODE]: 'Code investigation',
  [AtlasAITool.DRAFT_DOC]: 'Draft Doc',
  [AtlasAITool.SALESFORCE]: 'Salesforce',
  [AtlasAITool.BRAVE_SEARCH]: 'Web',
  [AtlasAITool.FIRECRAWL_SCRAPE]: 'Page',
  [AtlasAITool.SEARCH_SESSIONS]: 'Past Chats',
  [AtlasAITool.EXECUTE_SERVICE]: 'Service',
  [AtlasAITool.DESCRIBE_SERVICE]: 'Service Info',
  [AtlasAITool.READ_PROMPT]: 'Prompt',
  [AtlasAITool.SEARCH_PROMPT]: 'Prompt',
  [AtlasAITool.WRITE_PROMPT]: 'Prompt',
  [AtlasAITool.EXECUTE_WORKFLOW]: 'Workflow',
  [AtlasAITool.DESCRIBE_WORKFLOW]: 'Workflow Info',
  [AtlasAITool.RENDER_CHART]: 'Chart',
  [AtlasAITool.SUBMIT_FINAL_ANSWER]: 'Answer',
  [AtlasAITool.SPAWN_SUBAGENT]: 'Subagents',
  [AtlasAITool.CHECK_SUBAGENTS]: 'Subagents',
  [AtlasAITool.REDIRECT_SUBAGENT]: 'Steer',
  [AtlasAITool.CANCEL_SUBAGENT]: 'Cancel',
  [AtlasAITool.DEFER_RESPONSE]: 'Wait',
  [AtlasAITool.CREATE_PLAN]: 'Plan',
  [AtlasAITool.UPDATE_PLAN_STEP]: 'Plan Step',
  [AtlasAITool.READ_PLAN]: 'Plan',
  [AtlasAITool.OPEN_PAGE]: 'Open Page',
  [AtlasAITool.REVISE_REPORT]: 'Revise Report',
}

/**
 * Labels for the chat agent's grouped tools, which a step trace records by the name the model
 * called: a merged tool (`memory`, `plan`…) or a service toolkit (`atlas_tasks`, `gmail`…).
 */
const GROUPED_TOOL_LABELS: Record<string, string> = {
  memory: 'Memory',
  saved_prompts: 'Prompt',
  web: 'Web',
  code: 'Code',
  subagents: 'Subagents',
  plan: 'Plan',
  atlas_tasks: 'Tasks',
  aim: 'Messages',
  notes: 'Notes',
  box: 'Box',
  gmail: 'Gmail',
  calendar: 'Calendar',
  google_drive: 'Google Drive',
  crm: 'CRM',
  asana: 'Asana',
  arc_settings: 'Arc Settings',
  engineering: 'Engineering',
  credit: 'Credit',
  portal: 'Portal',
}

/**
 * Resolve a tool name (the raw wire value from websocket events) to its
 * human label. Falls back to the raw name for unknown tools so we never hide
 * a tool completely.
 */
export function toolLabel(name: string): string {
  return ATLAS_AI_TOOL_LABELS[name as AtlasAITool] ?? GROUPED_TOOL_LABELS[name] ?? name
}

/**
 * Narrow a raw tool name (the wire value from SDK tool-use blocks / websocket
 * events) to an AtlasAITool — or undefined when it isn't one (server-side tools
 * like 'web_search'/'code_execution', or model output not in the enum). Lets call
 * sites compare against AtlasAITool members without an unsound `as` cast.
 */
export const asAtlasTool = enumNarrow(AtlasAITool).as

// Mention types for Atlas AI chat
export enum AtlasAIMentionType {
  Client = 'Client',
  Entity = 'Entity',
  BBC = 'BBC',
  OngoingReporting = 'OngoingReporting',
  DueDiligence = 'DueDiligence',
  Prospect = 'Prospect',
  Contact = 'Contact',
  Opportunity = 'Opportunity',
  Company = 'Company',
  Organization = 'Organization',
  // The in-view amendment analysis (general_analyses row). Surfaced as a chat
  // chip from the Amendment Analysis page so Arc targets the right amendment
  // for commentary edits.
  AmendmentAnalysis = 'AmendmentAnalysis',
}

// Atlas AI mention interface
export interface IAtlasAIMention {
  id: string
  type: MentionType
  name: string
  metadata?: Record<string, any>
  /**
   * Channel messages only. The broadcast token(s) — `@here`/`@channel` — whose
   * send-time audience this user was in. Set by channels/resolveBroadcastMentions
   * on the entries it appends; absent on a chip the author actually typed.
   *
   * This is what lets an edit tell the two apart: a broadcast token that survives
   * an edit keeps the audience it was sent to rather than re-resolving against
   * whoever happens to be online at edit time, and a token the edit deletes takes
   * exactly its own recipients with it. Server-set only — extractMentions rebuilds
   * every mention from `{id, type, name}`, so a client can't put this in a body.
   *
   * A list, not one value: a message using both tokens puts a recipient in two
   * audiences, and removing one must not drop them while the other stands.
   */
  broadcast?: BroadcastMentionType[]
  /**
   * Channel messages only. The ids of the tagged teams whose send-time roster
   * this user was on — `broadcast`'s counterpart for team chips, set by
   * channels/resolveTeamMentions and server-set only for the same reason.
   */
  teams?: string[]
  /**
   * Channel messages only. The author typed this person's chip, so they were named
   * directly even if an `@here` or team chip also reached them. Server-set only, by
   * channels/resolveTextMentions, for the same reason as `broadcast`.
   */
  typed?: boolean
}

// Atlas AI suggestion interface for autocomplete
export interface IAtlasAISuggestion {
  id: string
  type: MentionType
  name: string
  link: string | null
  avatar: string
  clientStatus?: ClientInfoStatus
  recordDate?: string
  reportType?: string
  /** For people (users and CRM contacts), so a picker can invite them by email. */
  email?: string | null
  /** Internal users only: a bot account, labelled as one in the picker. */
  isBot?: boolean
}

/**
 * Human-readable preview of a WRITE_DANGEROUS action, rendered in the approval
 * card so the user can make an informed decision without seeing raw params.
 *
 * Each @aiCallable service can opt in by defining
 *   `static async buildApprovalPreview(params, { user }): Promise<IApprovalPreview>`
 * and the server stores the returned shape on the agent_approval row.
 *
 * Kept intentionally generic — title/subtitle/fields/warnings — so every
 * service renders through the same client component, but the copy varies.
 */
export interface IApprovalPreview {
  /** Headline, e.g. "Delete: Testing Arc's Scheduler" */
  title: string
  /** One-liner under the title, e.g. "Apr 23, 2026 2:00–2:30 PM EDT" */
  subtitle?: string
  /**
   * Labeled key-value rows, e.g. [{ label: 'Attendees', value: 'Bob, Kat' }].
   * - `href` turns the value into a link (opened in a new tab).
   * - `edit` makes the row editable inline ("Accept with Edits"): the card shows
   *   a pencil next to the value, and clicking it swaps the value for an input.
   *   On accept, Approve merges the edited value over the stored params (and the
   *   target service re-validates). The server's edit allow-list is derived from
   *   these `edit.param` keys, so only declared rows can be overridden.
   * - `showWhen` conditionally renders the row only when another param's current
   *   (possibly edited) value matches — e.g. a Follow-Up row that appears only
   *   while Status is "track". Always-included-but-conditional rows let an edit
   *   to one field reveal a dependent field that the original snapshot hid.
   */
  fields?: Array<{
    label: string
    value: string
    href?: string
    edit?: IApprovalFieldEdit
    showWhen?: { param: string; equals: string }
  }>
  /** Prominent warnings, e.g. "Cancellation emails will go to 2 attendees." */
  warnings?: string[]
}

/** Inline-edit config for a single approval-card field. See IApprovalPreview.fields. */
export interface IApprovalFieldEdit {
  /** Key in the approval's `params` this field overrides on accept. */
  param: string
  /** Input rendered when the row is being edited. */
  type: 'text' | 'select'
  /** Allowed values for `type: 'select'`. `label` defaults to `value` when omitted. */
  options?: Array<{ value: string; label?: string }>
}

/**
 * Lifecycle states for a WRITE_DANGEROUS approval row. Persisted to
 * agent_approval.status; values must stay identical to the DB enum.
 */
export enum ApprovalStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  Executed = 'executed',
  Failed = 'failed',
}

/**
 * Transient client-side state while an approve/reject request is in flight.
 * Not persisted — only lives in redux between REQUEST and SUCCESS/FAILURE.
 */
export enum ApprovalResolvingStatus {
  Approving = 'approving',
  Rejecting = 'rejecting',
}

/**
 * Wire shape of a pending approval as returned by the /atlasai/approvals list
 * endpoint and stored in client redux state.
 */
export interface IApprovalSummary {
  id: string
  sessionId: string | null
  /** Chat message this approval was queued from. Used to render the resolved
   * outcome inline with the originating assistant message. */
  atlasChatHistoryId: string | null
  /** Task whose Arc session raised this, when it came from a task comment. The
   * Task detail panel scopes its cards on this the way the chat scopes on
   * sessionId. Null for approvals raised anywhere else. */
  taskId: string | null
  serviceName: string
  params: Record<string, unknown>
  preview?: IApprovalPreview | null
  status: ApprovalStatus
  createdAt: string
}

/**
 * Client-only shape of the pendingApprovals redux slice.
 */
export interface IPendingApprovalsState {
  pending: IApprovalSummary[]
  resolving: Record<string, ApprovalResolvingStatus>
}

/**
 * Outcome of one bulk approve/reject, per approval.
 *
 * A bulk run resolves each card through the same single-item endpoint, so one
 * failure leaves the rest untouched — which is only useful if the UI can say
 * which was which. Failed ids keep their card on screen with `message` beside it.
 */
export interface IBulkApprovalResult {
  succeededIds: string[]
  failures: Array<{ approvalId: string; message: string }>
}

/**
 * Wire shape of a resolved approval, returned on chat history fetch attached
 * to the assistant message (`atlas_chat_history.id`) that queued it. Enables
 * the chat UI to render "Approved · CreateEvent" / "Rejected · DeleteEvent"
 * strips inline with the message — without a second API call.
 *
 * Only terminal statuses are returned here. Pending approvals are served by
 * the existing /atlasai/approvals list endpoint and the floating
 * PendingApprovals card.
 */
export interface IMessageApproval {
  id: string
  serviceName: string
  status: ApprovalStatus
  preview: IApprovalPreview | null
  params: Record<string, unknown>
  result: unknown
  error: string | null
  createdAt: string
  resolvedAt: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Workflow catalog — read-only registry entry shape used by the Workflows
// page. Workflow-specific payload shapes live in per-workflow interface files
// (e.g. opsWriteup.ts), not here.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wire shape of one workflow registered in code (the registry catalog entry).
 * Used by the Workflows page to render the read-only list alongside
 * user-authored prompts.
 */
export interface IWorkflowCatalogEntry {
  workflowName: string
  description: string
}

/**
 * A user-authored prompt (slash-command procedure) that is "active" for the
 * current conversation — i.e. its `/key` was invoked in the current message or
 * an earlier turn still in the context window. Resolved server-side every turn
 * and injected into the system prompt so its steps and routing survive multi-
 * step follow-ups (the read_prompt tool result lives only in the transient
 * agent-loop message array and is never persisted). `value` is the raw prompt
 * body stored in agent_memory — typically
 * `{ trigger, description, steps: [{ order, title, detail }], outputs?, defaultBoxLocation? }`
 * but kept `unknown` because the column is untyped JSONB.
 */
export interface IActivePrompt {
  key: string
  value: unknown
}

export enum ArcAutomationRunStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * Lifecycle of a single detached subagent run (`arc_subagent_run.status`).
 * PENDING = row inserted, job enqueued, not yet picked up; RUNNING = executing;
 * COMPLETED/FAILED = terminal with/without a result; CANCELLED = killed by the
 * parent (Stop) or `cancel_subagent`. Values must match the DB varchar exactly.
 */
export enum ArcSubagentRunStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/** Terminal states — no further transitions, and no wake should re-fire. */
export const TERMINAL_SUBAGENT_STATUSES: ReadonlySet<ArcSubagentRunStatus> = new Set([
  ArcSubagentRunStatus.COMPLETED,
  ArcSubagentRunStatus.FAILED,
  ArcSubagentRunStatus.CANCELLED,
])

/**
 * What a detached `arc_subagent_run` row actually runs. Both kinds share the row lifecycle
 * (claim, cancel, wake, sweep, live panel); only the worker's model call differs.
 */
export enum ArcSubagentRunKind {
  /** An Arc subagent — AgentRunner with the subagent tool set. */
  SUBAGENT = 'subagent',
  /** A code-agent investigation (`services/codeAgent`) in a checkout of this repo. */
  CODE_INVESTIGATION = 'code_investigation',
}

/** A subagent still doing work (queued or running) — the inverse of terminal.
 *  Single source of truth for "is this subagent active", shared by the wake
 *  logic, the status report, and the client panel/reducer. */
export const isActiveSubagentStatus = (status: ArcSubagentRunStatus): boolean =>
  !TERMINAL_SUBAGENT_STATUSES.has(status)

/** Human-readable label per status. Single source of truth shared by the
 *  client status panel (used as-is) and the server's model-facing status
 *  report (upper-cased). */
export const SUBAGENT_STATUS_LABEL: Record<ArcSubagentRunStatus, string> = {
  [ArcSubagentRunStatus.PENDING]: 'Queued',
  [ArcSubagentRunStatus.RUNNING]: 'Running',
  [ArcSubagentRunStatus.COMPLETED]: 'Completed',
  [ArcSubagentRunStatus.FAILED]: 'Failed',
  [ArcSubagentRunStatus.CANCELLED]: 'Cancelled',
}

/**
 * Lightweight per-subagent status pushed to the chat UI over the
 * ATLAS_AI_SUBAGENT_STATUS websocket event and returned by the check_subagents
 * tool. Deliberately omits the full `result` text (large; delivered via the
 * wake turn / check_subagents' own report) so the live stream stays cheap.
 */
export interface ISubagentStatus {
  id: string
  /** Short, human-readable name for the subagent (2-5 words), set by the
   *  spawning agent. Null for legacy runs created before labels existed —
   *  the UI falls back to the task text. */
  label: string | null
  task: string
  status: ArcSubagentRunStatus
  turns: number
  progressText: string | null
}

/**
 * Lifecycle of a persisted multi-step plan — the SERIAL, dependency-chained
 * counterpart to `spawn_subagent`'s parallel fan-out. Values must match the DB
 * varchar exactly.
 *
 * ACTIVE is the only status that auto-continues: when a turn ends with steps
 * still pending and nothing blocking, the session is resumed automatically so
 * the chain keeps moving without the user having to prompt it along. The other
 * non-terminal statuses are deliberate stopping points that hand control back:
 * AWAITING_INPUT (the agent hit a question only the user can answer) and PAUSED
 * (a budget ceiling tripped, or a step failed outright).
 */
export enum ArcPlanStatus {
  ACTIVE = 'active',
  AWAITING_INPUT = 'awaiting_input',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  ABANDONED = 'abandoned',
}

/** Terminal states — the plan is over; no resume should ever re-fire. */
export const TERMINAL_PLAN_STATUSES: ReadonlySet<ArcPlanStatus> = new Set([
  ArcPlanStatus.COMPLETED,
  ArcPlanStatus.ABANDONED,
])

/** A plan that still has life in it — shown in the live panel, and re-armed by
 *  the user's next message. Not the same as auto-resumable (see below). */
export const isActivePlanStatus = (status: ArcPlanStatus): boolean =>
  !TERMINAL_PLAN_STATUSES.has(status)

/** The array form of `isActivePlanStatus`, for the `In(...)` clauses that select
 *  a session's live plan. Derived from the enum rather than spelled out so a new
 *  non-terminal status joins every one of those queries automatically. */
export const LIVE_PLAN_STATUSES: readonly ArcPlanStatus[] =
  Object.values(ArcPlanStatus).filter(isActivePlanStatus)

/** The halted-but-alive statuses — a plan the user's next message re-arms. */
export const HALTED_PLAN_STATUSES: readonly ArcPlanStatus[] = [
  ArcPlanStatus.AWAITING_INPUT,
  ArcPlanStatus.PAUSED,
]

/**
 * Whether the continuation path should fire on its own. Deliberately narrower
 * than `isActivePlanStatus`: an AWAITING_INPUT or PAUSED plan is still "alive"
 * for the UI and for the user's next message to revive, but it must NEVER
 * auto-resume — that's exactly the interrupt the user asked for.
 */
export const isAutoResumablePlanStatus = (status: ArcPlanStatus): boolean =>
  status === ArcPlanStatus.ACTIVE

export const PLAN_STATUS_LABEL: Record<ArcPlanStatus, string> = {
  [ArcPlanStatus.ACTIVE]: 'In progress',
  [ArcPlanStatus.AWAITING_INPUT]: 'Needs your input',
  [ArcPlanStatus.PAUSED]: 'Paused',
  [ArcPlanStatus.COMPLETED]: 'Completed',
  [ArcPlanStatus.ABANDONED]: 'Abandoned',
}

/**
 * Per-step lifecycle. BLOCKED is the interrupt signal: the agent records a
 * question on the step instead of guessing, which stops auto-resume and hands
 * the turn back to the user. Making that a durable fact on the row (rather than
 * inferring intent from the turn's prose) is what lets the continuation path be
 * a deterministic branch on DB state.
 */
export enum ArcPlanStepStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  BLOCKED = 'blocked',
  SKIPPED = 'skipped',
  FAILED = 'failed',
}

/** Steps that will never run again — the plan's progress denominator. */
export const TERMINAL_PLAN_STEP_STATUSES: ReadonlySet<ArcPlanStepStatus> = new Set([
  ArcPlanStepStatus.COMPLETED,
  ArcPlanStepStatus.SKIPPED,
  ArcPlanStepStatus.FAILED,
])

/** A step still to be done (pending, mid-flight, or waiting on the user). */
export const isOutstandingPlanStep = (status: ArcPlanStepStatus): boolean =>
  !TERMINAL_PLAN_STEP_STATUSES.has(status)

export const PLAN_STEP_STATUS_LABEL: Record<ArcPlanStepStatus, string> = {
  [ArcPlanStepStatus.PENDING]: 'To do',
  [ArcPlanStepStatus.IN_PROGRESS]: 'Working',
  [ArcPlanStepStatus.COMPLETED]: 'Done',
  [ArcPlanStepStatus.BLOCKED]: 'Blocked',
  [ArcPlanStepStatus.SKIPPED]: 'Skipped',
  [ArcPlanStepStatus.FAILED]: 'Failed',
}

/**
 * One step as pushed to the live UI. Carries only a short `resultPreview`, not
 * the step's full result text — the same reasoning as `ISubagentStatus`: the
 * full text goes to the model via the status report, while the live stream
 * stays cheap enough to push on every checkpoint.
 */
export interface IArcPlanStepStatus {
  id: string
  ordinal: number
  title: string
  status: ArcPlanStepStatus
  attempts: number
  resultPreview: string | null
}

/**
 * Live plan state pushed over ATLAS_AI_PLAN_STATUS. Includes the budget
 * counters so the UI can show how much of its leash the plan has used — a plan
 * that quietly burns $20 without the user being able to see it coming is the
 * failure mode the caps exist to prevent.
 */
export interface IArcPlanStatus {
  id: string
  title: string
  status: ArcPlanStatus
  steps: IArcPlanStepStatus[]
  autoResumes: number
  maxAutoResumes: number
  estimatedCostUsd: number
  maxCostUsd: number
  /** The question that stopped the plan. Set only when AWAITING_INPUT. */
  blockedQuestion: string | null
  /** Why the plan paused (budget ceiling, failed step). Set only when PAUSED. */
  pausedReason: string | null
}

export enum ArcAutomationTriggerType {
  SCHEDULE = 'schedule',
  EVENT = 'event',
  /**
   * A generic, event-catalog-independent condition stated in natural language
   * (the watch's `prompt`). On an interval the `PollArcWatches` poller runs a
   * cheap, read-only agent turn ("the evaluation") that uses its tools to decide
   * whether the condition has occurred since the watch was armed; on the first
   * met verdict the watch resumes its source conversation and deactivates. Only
   * ever used by one-shot chat watches (`oneShot = true`), never by user-facing
   * Automations.
   */
  POLL = 'poll',
}

/** Owner (creator) summary attached to automations in list responses so admins,
 * who see every user's automations, can tell whose is whose. */
export interface IArcAutomationOwner {
  id: string
  /** Read by `displayName`, which every owner label goes through — a payload
   * that omits it silently falls back to the legal name. */
  nickname: string | null
  firstName: string | null
  lastName: string | null
  email: string
}

export interface IArcAutomation {
  id: string
  userId: string
  name: string
  prompt: string
  promptContentState: RawDraftContentState | null
  triggerType: ArcAutomationTriggerType
  /** Required for SCHEDULE triggers; null for EVENT triggers. */
  cronExpression: string | null
  /** Selected objects (activity types + reporting doc types) for EVENT triggers. */
  eventObjects: string[] | null
  /** Lifecycle status the selected objects fire on for EVENT triggers; null for SCHEDULE. */
  eventStatus: string | null
  /** Optional narrowing for EVENT triggers (e.g. { clientNames: [...] }). */
  eventFilters: IArcEventFilters | null
  /** Users who receive the run's session + notification. Empty/null = creator only. */
  recipientUserIds: string[] | null
  model: Models
  isActive: boolean
  /**
   * When true (default), the automation re-arms after every fire — schedules
   * recompute their next tick, events keep matching. When false, it is a
   * one-time Automation: it fires exactly once (at nextRunAt for SCHEDULE, or
   * on the first outbox match for EVENT) then deactivates itself, while staying
   * visible in the Automations list and spawning a new session like any other.
   */
  recurring: boolean
  nextRunAt: string | null
  lastRunAt: string | null
  createdAt: string
  updatedAt: string
  /** Populated by the list and get endpoints (admin Owner column / label); omitted elsewhere. */
  owner?: IArcAutomationOwner | null
}

export interface IArcAutomationRun {
  id: string
  automationId: string
  sessionId: string | null
  status: ArcAutomationRunStatus
  scheduledAt: string
  startedAt: string | null
  completedAt: string | null
  responseSnippet: string | null
  errorDetails: string | null
  notificationSent: boolean
  createdAt: string
}

export interface IArcAutomationWithRuns extends IArcAutomation {
  runs: IArcAutomationRun[]
}

// The dream cycle flags contradictions/duplicates it shouldn't auto-resolve
// (stored as dream_cycle_action rows). Surfaced on the memories admin UI so a
// human can review; `reason` is self-contained (it names the conflicting
// memories). Cleared when the flagged memory is edited.
export type AgentMemoryFlagType = 'contradiction_flagged' | 'duplicate_flagged'

export interface IAgentMemoryFlag {
  actionType: AgentMemoryFlagType
  reason: string
  createdAt: string
}
